import * as XLSX from 'xlsx-js-style';

export function exportYhteenvetoToExcel({
  sub,
  years,
  currentYear,
  tekniikkaYhteensa = [],
  lviYhteensa = [],
  sahkoYhteensa = [],
  tutkimusYhteensa = [],
  filename
}) {
  const GREEN = '04AA00';
  const WHITE = 'FFFFFF';
  const BORDER = 'DDDDDD';

  const colCount = 2 + years.length; // Osa-alue | Yhteensä | years...

  const normalizeValue = (v) => {
    if (v === '' || v === null || v === undefined || v === '0' || v === 0) {
      return '';
    }
    const n = Number(v);
    return Number.isNaN(n) ? '' : n;
  };

  const getValuesToShow = (item) => {
    if (sub?.name === 'Yhteenvetotaulukko') {
      if (item.label === 'Rakennetekniikka') return tekniikkaYhteensa;
      if (item.label === 'LVI Järjestelmät') return lviYhteensa;
      if (item.label === 'Sähköjärjestelmät') return sahkoYhteensa;
      if (item.label === 'Lisätutkimukset') return tutkimusYhteensa;
    }
    return item.values || [];
  };

  const getColumnTotals = () => {
    const items = (sub?.items || []).filter(item => item.label !== 'Yhteensä');

    return Array.from({ length: years.length }, (_, yearIdx) => {
      return items.reduce((sum, item) => {
        const values = getValuesToShow(item);
        const num = parseFloat(values?.[yearIdx]);
        return !isNaN(num) ? sum + num : sum;
      }, 0);
    });
  };

  const rows = [];
  const bodyRowMeta = [];

  // Super header
  rows.push([
    'Toimenpide-ehdotukset yhteensä',
    `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`,
    ...Array(years.length).fill('')
  ]);

  // Header row
  rows.push([
    'Osa-alue',
    'Yhteensä',
    ...years.map(String)
  ]);

  // Body rows
  (sub?.items || [])
    .filter(item => item.label !== 'Yhteensä')
    .forEach((item) => {
      const valuesToShow = getValuesToShow(item).map(normalizeValue);
      const rowTotal = valuesToShow.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const rowIndex = rows.length;

      bodyRowMeta.push({
        rowIndex,
        valuesToShow
      });

      rows.push([
        item.label || '',
        rowTotal === 0 ? '' : rowTotal,
        ...valuesToShow
      ]);
    });

  // Totals row
  const colTotals = getColumnTotals().map(normalizeValue);
  const grandTotal = colTotals.reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const totalRowIdx = rows.length;

  rows.push([
    'YHTEENSÄ',
    grandTotal === 0 ? '' : grandTotal,
    ...colTotals
  ]);

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Merge title row like the React table header feel:
  // A1 only title, B1..last merged cost text
  ws['!merges'] = [
    { s: { r: 0, c: 1 }, e: { r: 0, c: colCount - 1 } }
  ];

  ws['!cols'] = [
    { wch: 28 }, // Osa-alue
    { wch: 12 }, // Yhteensä
    ...years.map(() => ({ wch: 9 }))
  ];

  ws['!rows'] = rows.map((_, i) => {
    if (i === 0 || i === 1) return { hpt: 20 };
    return { hpt: 18 };
  });

  const baseBorder = {
    top: { style: 'thin', color: { rgb: BORDER } },
    bottom: { style: 'thin', color: { rgb: BORDER } },
    left: { style: 'thin', color: { rgb: BORDER } },
    right: { style: 'thin', color: { rgb: BORDER } }
  };

  const greenHeaderStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const topLeftTitleStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const superHeaderStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'right', vertical: 'center', wrapText: false },
    border: baseBorder
  };

  const labelStyle = {
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const totalColStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const numberStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const positiveValueStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const totalsStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: baseBorder
  };

  // Row 0
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[addr]) continue;

    if (c === 0) {
      ws[addr].s = topLeftTitleStyle;
    } else {
      ws[addr].s = superHeaderStyle;
    }
  }

  // Row 1
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: 1, c });
    if (!ws[addr]) continue;
    ws[addr].s = greenHeaderStyle;
  }

  // Body
  bodyRowMeta.forEach(({ rowIndex, valuesToShow }) => {
    const labelAddr = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
    if (ws[labelAddr]) ws[labelAddr].s = labelStyle;

    const totalAddr = XLSX.utils.encode_cell({ r: rowIndex, c: 1 });
    if (ws[totalAddr]) {
      const v = ws[totalAddr].v;
      ws[totalAddr].s =
        v !== '' && Number(v) > 0
          ? positiveValueStyle
          : totalColStyle;
    }

    valuesToShow.forEach((v, idx) => {
      const addr = XLSX.utils.encode_cell({ r: rowIndex, c: idx + 2 });
      if (!ws[addr]) return;

      ws[addr].s =
        v !== '' && Number(v) > 0
          ? positiveValueStyle
          : numberStyle;
    });
  });

  // Totals row
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: totalRowIdx, c });
    if (ws[addr]) ws[addr].s = totalsStyle;
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Yhteenveto');

  const outFilename = filename || `PTS_Yhteenveto_${currentYear}.xlsx`;
  XLSX.writeFile(wb, outFilename);
}